import { BetterSupportIcon, FeatureParityIcon, HonestPricingIcon, NoLockInIcon, TelemetryPatternIcon, ZeroConfigIcon } from "../icons-v2";
import type {
  ComparisonSection,
  ComparisonTableRow,
  SwitchStatsSection,
} from "./comparison-types";

export type {
  ComparisonColumnContent,
  ComparisonSection,
  ComparisonTableCell,
  ComparisonTableRow,
  PricingRow,
  SwitchStatItem,
  SwitchStatsSection,
} from "./comparison-types";

export const GRAFANA_COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    id: "full-stack-observability",
    title: "Full-Stack Observability",
    middleware: {
      description:
        "APM, Infrastructure, Logs, Distributed Tracing, RUM, Synthetic Monitoring, Databases, Containers, Kubernetes, Serverless, and LLM Observability. If you can monitor it in Grafana, you can monitor it here.",
      bullets: [
        "Every pillar included, one plan",
        "OpenTelemetry-native, not bolted-on",
        "OpsAI included, not a per-host add-on",
      ],
      image: "/images/comparison/feature-parity-middleware.webp",
    },
    competitor: {
      description:
        "Loki for logs, Mimir for metrics, Tempo for traces, Pyroscope for profiling, plus k6, IRM, and Grafana Assistant. Four backends stitched at the dashboard layer.",
      bullets: [
        "Four separately billed backends",
        "Four query languages (LogQL, PromQL, TraceQL, profile)",
        "Grafana Assistant billed per active AI user",
      ],
      image: "/images/comparison/full-stack-obs-grafana.webp",
    },
    icon: FeatureParityIcon,
  },
  {
    id: "no-lock-in",
    title: "No Vendor Lock-In",
    middleware: {
      description:
        "Middleware built on OpenTelemetry from day one. Portable instrumentation, no proprietary agent, and one timeline where logs, metrics, traces, and events correlate automatically.",
      bullets: [
        "Standard OTel collectors, no fork",
        "Cloud, on-prem, BYOC, or hybrid",
        "Switch vendors without re-instrumenting",
      ],
      image: "/images/comparison/no-lock-in-middleware.webp",
    },
    competitor: {
      description:
        "OpenTelemetry-friendly, but the workflow lives inside the LGTM stack. Moving off touches dashboards, alert rules, IRM workflows, and Assistant configurations across four products.",
      bullets: [
        "LGTM-anchored workflows",
        "Cross-signal correlation depends on data source config",
        "Migration touches every product",
      ],
      image: "/images/comparison/no-vendor-lock-grafana.webp",
    },
    icon: NoLockInIcon,
  },
  {
    id: "better-support",
    title: "Better Support and Experience",
    middleware: {
      description:
        "Every Middleware customer gets responsive, hands-on support, including a 1-hour SLA for critical issues. Dedicated shared Slack and Teams channels connect you directly with the engineers who built the product.",
      bullets: [
        "1-hour critical-issue response SLA",
        "Direct Slack / Teams channels",
        "Self-serve signup, no mandatory sales motion",
      ],
    },
    competitor: {
      description:
        "The Pro plan includes 8×5 email support. Premium Support and a dedicated Observability Architect are available exclusively with the Enterprise plan, which starts at a minimum annual commitment of $25K.",
      bullets: [
        "8×5 email on Pro",
        "Premium support gated to enterprise",
        "Community forums for lower tiers",
      ],
    },
    icon: BetterSupportIcon,
  },
  {
    id: "honest-pricing",
    title: "Honest Pricing",
    middleware: {
      description:
        "Simple, transparent pricing based on the data you actually send. There are no surprise charges for monthly traffic spikes, and OpsAI is bundled into the platform instead of being billed per host.",
      bullets: [
        "Pay per GB of data sent",
        "OpsAI included with token-based price",
        "180-day log retention bundled",
      ],
    },
    competitor: {
      description:
        "Grafana charges per active user, Mimir per 1K active series, Loki & Tempo per GB, Pyroscope per GB, k6 per virtual user hour, IRM per active user, and Assistant at $20/active AI user + $2 per 1M extra tokens",
      bullets: [
        "7+ overlapping meters",
        "Cardinality billed on Mimir",
        "Enterprise $25K/year minimum",
      ],
    },
    icon: HonestPricingIcon,
  },
  {
    id: "zero-config",
    title: "Zero Config",
    middleware: {
      description:
        "One install script, a lightweight OpenTelemetry Collector, and full-stack visibility in under a minute. Auto-instrumentation, service discovery, and Ingestion Control to drop noise in two clicks.",
      bullets: [
        "One-line install, no implementation engineer",
        "Auto service + dependency discovery",
        "No proprietary query language to learn",
      ],
      image: "/images/comparison/zero-config-middleware.webp",
    },
    competitor: {
      description:
        "Each backend needs its own data source, retention policy, and query language. Assistant helps write queries, but the four-backend operational complexity remains.",
      bullets: [
        "Separate config per backend",
        "Four query languages to maintain",
        "Cardinality + retention controls per meter",
      ],
      image: "/images/comparison/zero-config-grafana.webp",
    },
    icon: ZeroConfigIcon,
  },
  {
    id: "telemetry-patterns",
    title: "Telemetry Patterns",
    middleware: {
      description:
        "Service-level alerting on real metrics. Search every span you've sent. Click any log, jump to the trace. Slice by any high-cardinality dimension without watching a per-1K-series meter climb.",
      bullets: [
        "Service-level RED metrics on every operation",
        "Full-history span search, not last 15 minutes",
        "Logs-to-traces correlation for OTel data",
      ],
    },
    competitor: {
      description:
        "Powerful when tuned, but cross-signal correlation depends on how well data sources are configured, and high-cardinality slicing hits the Mimir meter directly at scale.",
      bullets: [
        "Cardinality directly hits Mimir billing",
        "Cross-source correlation is manual to configure",
        "Assistant Investigations still in public preview",
      ],
    },
    icon: TelemetryPatternIcon,
  },
];

export const GRAFANA_FEATURE_TABLE_ROWS: ComparisonTableRow[] = [
  {
    id: "bottom-line",
    label: "Bottom line",
    subtitle: "Who each tool is really built for",
    middleware: {
      text: "Teams that want unified full-stack observability with an SRE Agent built in",
    },
    competitor: {
      text: "Teams invested in the open-source LGTM stack",
    },
  },
  {
    id: "sre-agent",
    label: "SRE Agent ",
    subtitle: "(detect → fix → PR)",
    middleware: {
      status: "check",
      description: "OpsAI",
    },
    competitor: {
      status: "cross",
      description: "Grafana query copilot assistant",
    },
  },
  {
    id: "auto-remediation",
    label: "Auto-remediation ",
    subtitle: "(code + K8s)",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "pr-generation",
    label: "PR generation",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
    },
  },
  {
    id: "third-party-alert-ingestion",
    label: "Third-party alert ingestion",
    middleware: {
      status: "check",
      description: "Datadog, Grafana, Dynatrace",
    },
    competitor: {
      status: "cross",
      description: "Native only",
    },
  },
  {
    id: "byoc-on-prem",
    label: "BYOC / on-prem",
    middleware: {
      status: "check",
    },
    competitor: {
      status: "cross",
      description: "Enterprise only",
    },
  },
  {
    id: "value-for-money",
    label: "Value for money",
    middleware: {
      description: "Single-meter pricing, OpsAI included",
    },
    competitor: {
      description: "7+ independent meters",
    },
  },
  {
    id: "backends",
    label: "Backends",
    middleware: {
      description: "Unified single platform",
    },
    competitor: {
      description: "LGTM + Pyroscope + k6 + IRM",
    },
  },
  {
    id: "query-language",
    label: "Query language",
    middleware: {
      description: "Unified, no proprietary DSL",
    },
    competitor: {
      description: "LogQL + PromQL + TraceQL + profile",
    },
  },
  {
    id: "ingestion-pipeline",
    label: "Ingestion pipeline",
    middleware: {
      status: "check",
    },
    competitor: {
      description: "Per-backend",
    },
  },
  {
    id: "high-cardinality-metrics",
    label: "Custom / high-cardinality metrics",
    middleware: {
      status: "check",
      description: "Bundled",
    },
    competitor: {
      headline: "Billed differently",
      headlineStyle: "bold",
      description: "Mimir bills per 1K active series",
    },
  },
  {
    id: "ai-pricing",
    label: "AI pricing",
    middleware: {
      status: "check",
      description: "Included",
    },
    competitor: {
      headline: "Billed differently",
      headlineStyle: "bold",
      description: "Assistant $20/active user + token overage",
    },
  },
  {
    id: "log-retention",
    label: "Log retention",
    middleware: {
      status: "check",
      description: "180 days included",
    },
    competitor: {
      status: "cross",
      description: "30 days on Pro, custom on Enterprise",
    },
  },
  {
    id: "annual-commitment",
    label: "Annual commitment",
    middleware: {
      headline: "None",
      headlineStyle: "gradient",
    },
    competitor: {
      headline: "$25K/year commit",
      headlineStyle: "bold",
    },
  },
  {
    id: "free-trial",
    label: "Free trial",
    middleware: {
      status: "check",
      description: "14 days, no credit card",
    },
    competitor: {
      status: "cross",
      description: "Free tier with per-product caps",
    },
  },
  {
    id: "dedicated-support",
    label: "Dedicated technical support",
    middleware: {
      status: "check",
      description: "All plans",
    },
    competitor: {
      status: "cross",
      description: "Enterprise only",
    },
  },
];

export const GRAFANA_SWITCH_STATS: SwitchStatsSection = {
  tag: "What customers get",
  title: "The numbers after the switch",
  description:
    "Averages across customers who migrated from Grafana Cloud to Middleware.",
  stats: [
    {
      id: "troubleshooting-speed",
      value: "4",
      unit: "×",
      title: "Troubleshooting",
      description:
        "Faster mean-time-to-detect with unified signals on one timeline.",
    },
    {
      id: "cost-reduction",
      value: "50",
      unit: "%",
      title: "Cost Reduction",
      description:
        "Reduction in observability spend versus Grafana Cloud contracts",
    },
    {
      id: "support-response",
      value: "1",
      unit: "hr",
      title: "Issue response SLA",
      description: "Critical-issue response SLA on every plan",
    },
    {
      id: "opsai-automation",
      value: "80",
      unit: "%",
      title: "Auto fixes with accuracy",
      description:
        "OpsAI resolves issues end-to-end, saving 60% of engineer time.",
    },
  ],
};
